import { mapIndexed } from '@@serverHelpers/index.mjs';
import { useFormContext } from 'react-hook-form';
import { applySpec, compose, defaultTo, isEmpty, isNil, map, path, pathOr, propOr } from 'ramda';
import {
  ControlledRadioButton,
  ControlledSelect
} from '@@viewCommonComponents/controlled/index.js';
import { isRequiredSelect } from '@@viewHelpers/validations/index.js';
import { InfoMessage } from '@fasstech/middle-design-system';
import { handleChangeDiscount } from '../helpers/handleChangeDiscount.mjs';
import { disableDiscountSelectButton } from '../helpers/disableDiscountSelectButton.mjs';

const DiscountsTable = ({ pricings, getMiddleText, getMiddleBoolean, beneficiaryIndex }) => {
  const form = useFormContext();

  const pricingsForm = form.watch(`offer.packs.${beneficiaryIndex}.pricings`);

  const getWaiverRequestsInfos = (index) =>
    applySpec({
      stage: path([index, 'stage']),
      textInformations: pathOr([], [index, 'waiverRequests', 0, 'textInformations']),
      textInformation: path([index, 'waiverRequests', 0, 'textInformation'])
    })(pricingsForm);

  const handleChangeStage = (value, supplementaryCover) => {
    if (!supplementaryCover) {
      pricingsForm.forEach((pricing, idx) => {
        if (
          propOr(false, 'supplementaryCover', pricing) &&
          propOr(false, 'isLinkedToStageProduct', pricing)
        ) {
          form.setValue(`offer.packs.${beneficiaryIndex}.pricings.${idx}.stage`, value);
        }
      });
    }
  };

  let atLeastOneStage = false;

  return (
    <div>
      {/* Desktop */}
      <div className="hidden md:flex">
        <div className="grid grid-cols-4 gap-4">
          <div className="border border-[#E7E7E7] rounded-lg p-4">
            {mapIndexed(({ id, moduleLabel, supplementaryCover }, index) => (
              <div
                key={id}
                className={`flex items-center h-[50px] p-12 ${index % 2 === 1 ? 'bg-[#F9F9FF] rounded-lg' : ''}`}>
                <div className="font-bold h-[40px]">
                  {moduleLabel}
                  <span className={'italic'}>
                    {supplementaryCover ? ' (renfort)' : ' (produit)'}
                  </span>
                </div>
              </div>
            ))(pricings)}
          </div>

          <div className="border border-[#E7E7E7] col-span-3 rounded-lg p-4">
            {mapIndexed((pricing, pricingIndex) => {
              const { id, availableWaiverRequests, supplementaryCover, isLinkedToStageProduct } =
                pricing;

              const discountOptions = compose(
                map((waiverRequest) => ({
                  value: waiverRequest,
                  label: waiverRequest
                })),
                defaultTo([])
              )(availableWaiverRequests);

              const pricingCanHaveWaivers = !isEmpty(discountOptions);

              const { textInformations, textInformation, stage } =
                getWaiverRequestsInfos(pricingIndex);

              if (!isNil(stage)) atLeastOneStage = true;

              return (
                <div
                  key={id}
                  className={`flex flex-row items-center h-[50px] p-12 ${pricingIndex % 2 === 1 ? 'bg-[#F9F9FF] rounded-lg' : ''}`}>
                  <div className="w-[350px] h-[40px]">
                    {getMiddleBoolean('STAGES_EST_NECESSAIRE') && !isNil(stage) ? (
                      <ControlledRadioButton
                        withIndicator={false}
                        name={`offer.packs.${beneficiaryIndex}.pricings.${pricingIndex}.stage`}
                        disabled={supplementaryCover && isLinkedToStageProduct}
                        options={[
                          {
                            label: getMiddleText('STAGES_VALEUR_BOUTON_OPTION1'),
                            value: true
                          },
                          {
                            label: getMiddleText('STAGES_VALEUR_BOUTON_OPTION2'),
                            value: false
                          }
                        ]}
                        form={form}
                        extraChange={(value) => handleChangeStage(value, supplementaryCover)}
                        booleanValue
                        data-testid={`STAGE_${pricingIndex}`}
                      />
                    ) : (
                      <div className="h-[50px]" />
                    )}
                  </div>

                  <div className="flex border-r border-[#E7E7E7] h-[50px] m-4" />

                  <div className="h-[40px] min-w-[200px]">
                    {getMiddleBoolean('REMISES_EST_NECESSAIRE') && pricingCanHaveWaivers ? (
                      <ControlledSelect
                        withIndicator={false}
                        name={`offer.packs.${beneficiaryIndex}.pricings.${pricingIndex}.waiverRequests.0.code`}
                        label={'Remise'}
                        disabled={disableDiscountSelectButton(pricing, {
                          pricingIndex,
                          discountOptions,
                          beneficiaryIndex
                        })}
                        form={form}
                        extraChange={(value) =>
                          handleChangeDiscount(
                            form,
                            value,
                            beneficiaryIndex,
                            supplementaryCover,
                            discountOptions,
                            textInformations,
                            pricingIndex
                          )
                        }
                        rules={isRequiredSelect(discountOptions)}
                        options={discountOptions}
                        data-testid={`REMISES_${pricingIndex}`}
                      />
                    ) : (
                      <div className="h-[50px]" />
                    )}
                  </div>
                  {getMiddleBoolean('REMISES_EST_NECESSAIRE') &&
                    pricingCanHaveWaivers &&
                    !isNil(textInformation) && (
                    <div className="flex-shrink-0 w-full max-w-[200px]">
                      <InfoMessage className={'text-sm'} iconName={'info_question'}>
                        {textInformation}
                      </InfoMessage>
                    </div>
                  )}
                </div>
              );
            })(pricings)}
            {getMiddleBoolean('STAGES_EST_NECESSAIRE') && atLeastOneStage && (
              <div className="w-[350px]">
                <InfoMessage iconName={'info_question'}>
                  {getMiddleText('STAGES_MESSAGE_INFO')}
                </InfoMessage>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="flex flex-col items-center gap-4 md:hidden">
        {mapIndexed((pricing, pricingIndex) => {
          const {
            id,
            moduleLabel,
            availableWaiverRequests,
            supplementaryCover,
            isLinkedToDiscountProduct,
            isLinkedToStageProduct
          } = pricing;

          const discountOptions = compose(
            map((waiverRequest) => ({
              value: waiverRequest,
              label: waiverRequest
            })),
            defaultTo([])
          )(availableWaiverRequests);

          const pricingCanHaveWaivers = !isEmpty(discountOptions);

          const { textInformations, textInformation, stage } = getWaiverRequestsInfos(pricingIndex);

          return (
            <div key={id}>
              <div className="border border-[#E7E7E7] rounded-lg p-4 flex flex-col items-center gap-4">
                <div className="font-bold">
                  {moduleLabel}
                  <span className={'italic'}>
                    {supplementaryCover ? ' (renfort)' : ' (produit)'}
                  </span>
                </div>

                {getMiddleBoolean('STAGES_EST_NECESSAIRE') && !isNil(stage) && (
                  <>
                    <ControlledRadioButton
                      withIndicator={false}
                      name={`offer.packs.${beneficiaryIndex}.pricings.${pricingIndex}.stage`}
                      disabled={supplementaryCover && isLinkedToStageProduct}
                      options={[
                        {
                          label: getMiddleText('STAGES_VALEUR_BOUTON_OPTION1'),
                          value: true
                        },
                        {
                          label: getMiddleText('STAGES_VALEUR_BOUTON_OPTION2'),
                          value: false
                        }
                      ]}
                      form={form}
                      extraChange={(value) => handleChangeStage(value, supplementaryCover)}
                      booleanValue
                      data-testid={`STAGE_${pricingIndex}`}
                    />
                    <InfoMessage iconName={'info_question'}>
                      {getMiddleText('STAGES_MESSAGE_INFO')}
                    </InfoMessage>
                  </>
                )}

                {getMiddleBoolean('REMISES_EST_NECESSAIRE') && pricingCanHaveWaivers && (
                  <ControlledSelect
                    withIndicator={false}
                    name={`offer.packs.${beneficiaryIndex}.pricings.${pricingIndex}.waiverRequests.0.code`}
                    label={'Remise'}
                    disabled={supplementaryCover && isLinkedToDiscountProduct}
                    form={form}
                    extraChange={(value) =>
                      handleChangeDiscount(
                        form,
                        value,
                        beneficiaryIndex,
                        supplementaryCover,
                        discountOptions,
                        textInformations,
                        pricingIndex
                      )
                    }
                    rules={isRequiredSelect(discountOptions)}
                    options={discountOptions}
                    data-testid={`REMISES_${pricingIndex}`}
                  />
                )}

                {getMiddleBoolean('REMISES_EST_NECESSAIRE') &&
                  pricingCanHaveWaivers &&
                  !isNil(textInformation) && (
                  <InfoMessage className={'w-[200px] text-sm'} iconName={'info_question'}>
                    {textInformation}
                  </InfoMessage>
                )}
              </div>
            </div>
          );
        })(pricings)}
      </div>
    </div>
  );
};

export default DiscountsTable;
